import { locale as getLang, region } from './utils.js'

// 返回数词所属的复数类型：zero/one/two/few/many/other
export const pluralType = (n) => {
  const localeLang = getLang().replace('_', '-') // 给 PluralRules 传的参数必须是 'en-US' 形式的，参考：https://developer.mozilla.org/zh-CN/docs/Web/JavaScript/Reference/Global_Objects/Intl#locales_%E5%8F%82%E6%95%B0
  try {
    if (
      process.client &&
      typeof Intl === 'object' &&
      typeof Intl.PluralRules === 'function'
    ) {
      return new Intl.PluralRules(localeLang).select(n)
    } else {
      return getPluralType(n)
    }
  } catch (e) {
    return getPluralType(n)
  }
}

// 入参 str: 形如 "{# content}..." 或 "{ content_A # content_B }..." 的字符串
// 入参 num: 替换 "#" 的数词变量
// 返回: str 第一个花括号内 # 被 num 替换后的 content
function getQuantifier(str, num = 0) {
  let res = ''
  for (let i = 0; i < str.length; i++) {
    if (i >= 1 && str[i - 1] === '{') {
      do {
        res += str[i]
        i++
      } while (str[i] !== '}')
    }
    if (str[i] === '}') {
      break
    }
  }
  // str.slice(str.indexOf('{')+1, str.indexOf('}'))
  return res.replace('#', num).trim()
}

// 入参 text：翻译组提供的区分单复数的本地化文案，例：
// First transaction by {amount, plural, zero {# people} one {# person} two {# people} few {# people} many {# people} other {# people}}
export const textFormat = (str, num = 0) => {
  if (!str) {
    return ''
  }
  const target = str.slice(str.indexOf('{'), str.lastIndexOf('}') + 1) // 1. 获取整个花括号里的内容（包含花括号）
  const type = pluralType(num) // 2. 获取变量对应的复数类型：zero/one/two/few/many/other

  // 3. 根据第2步结果获取目标量词
  const partStr = target.slice(target.indexOf(type) + type.length) // 若 type 为 other，则取 other 之后的字符串"{ 带 # 的内容 } ..."
  const quantifier = getQuantifier(partStr, num)
  // 4. 将第3步的结果替换整个花括号（第1步得到的内容）并返回
  return str.replace(target, quantifier)
}

/*
  该方法适用于一句文案中包含多个变量的情况
  入参： 
  - str 原文案
  - obj 变量对象：key 是变量名称，val 对应的数量
*/
export const textFormatWithMultiVars = (str, obj) => {
  // for (const [key, val] of Object.entries(obj)) {
  //   console.log(`${key}: ${val}`)
  // }
  let res = ''
  let tempInd = 0
  // 匹配花括号得到多个变量对应的待匹配文案
  const indexArr = getMatchedParenthesesIndex(str)
  for (let arr of indexArr) {
    const [startInd, endInd] = arr
    const target = str.slice(startInd, endInd) // {varName, plural, one {# content_A}, other {# content_B}}
    const varName = target.slice(1, target.indexOf(',')).trim()
    const num = obj[varName]

    const type = pluralType(num)
    const partStr = target.slice(target.indexOf(type) + type.length)
    const quantifier = getQuantifier(partStr, num)
    if (!res) {
      res = str.slice(0, startInd) + quantifier
    } else {
      res += str.slice(tempInd + 1, startInd) + quantifier
    }
    tempInd = endInd // 记录上一对花括号索引的结束位置
  }
  res += str.slice(tempInd + 1)
  return res
}

/*
  返回：二维数组，每一个数组的子项包含两个值，为最外层花括号所在的索引对
*/
function getMatchedParenthesesIndex(str) {
  const res = []
  const stack = []
  let startInd, endInd // 记录最外层花括号的起始索引与终止索引
  for (let i = 0; i < str.length; i++) {
    if (str[i] === '{') {
      stack.push(i)
      if (stack.length === 1) {
        startInd = i
      }
    } else if (str[i] === '}') {
      stack.pop()
      if (stack.length === 0) {
        endInd = i
      }
    }
    if (!isNaN(startInd) && !isNaN(endInd)) {
      res.push([startInd, endInd])
      startInd = undefined
      endInd = undefined
    }
  }
  if (stack.length > 0) {
    // console.log('出错了，有多余的花括号，请检查传入的翻译文案')
    return []
  }
  return res
}

// 自测
// const monthly_expenses =
//   'Bought {recharged_amount, plural, one {# K-coin} other {# K-coins}} | Spent {consumed_amount, plural, one {# K-coin} other {# K-coins}}'
// console.info(
//   textFormatWithMultiVars(monthly_expenses, {
//     recharged_amount: 367800,
//     consumed_amount: 1,
//   })
// )

function getPluralType(targetnum) {
  function onlyOther() {
    return 'other'
  }
  function oneOrOther() {
    if (Math.abs(targetnum) == 1) {
      return 'one'
    } else {
      return 'other'
    }
  }
  function oneOrOther_2() {
    if (Math.abs(targetnum) < 2 && Math.abs(targetnum) >= 0) {
      return 'one'
    } else {
      return 'other'
    }
  }
  const config = {
    br: oneOrOther_2,
    id: onlyOther,
    my: onlyOther,
    ph: oneOrOther, // 注意我们网站在菲律宾用的是英语，因此按照英语语法规则来
    in: oneOrOther,
    ru: function () {
      if (targetnum % 10 == 1 && targetnum % 100 != 11) {
        return 'one'
      } else if (
        [2, 3, 4].includes(targetnum % 10) &&
        ![12, 13, 14].includes(targetnum % 100)
      ) {
        return 'few'
      } else if (
        [0, 5, 6, 7, 8, 9].includes(targetnum % 10) ||
        [11, 12, 13, 14].includes(targetnum % 100)
      ) {
        return 'many'
      } else {
        return 'other'
      }
    },
    es: oneOrOther,
    fr: oneOrOther_2,
    de: oneOrOther,
    it: oneOrOther,
    th: oneOrOther,
    uz: oneOrOther,
  }
  const func = config[region()]
  return func ? func() : 'other'
}
